import axios from "axios";
import { toast } from "sonner";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "rudram_session_token";

export const authToken = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (t) => { if (t) localStorage.setItem(TOKEN_KEY, t); },
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

export const api = axios.create({
  baseURL: API,
  withCredentials: true, // keep the cookie as a fallback when it isn't blocked
});

// Attach the bearer token so auth survives blocked third-party cookies (preview iframe).
api.interceptors.request.use((config) => {
  const t = authToken.get();
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

// Remember where the user was so we can send them back after sign-in.
export function rememberRedirect() {
  try {
    if (typeof window !== "undefined") {
      sessionStorage.setItem("rud_post_login_redirect", window.location.pathname + window.location.search);
    }
  } catch (e) { /* noop */ }
}

// Persist any session token returned by an auth endpoint automatically.
api.interceptors.response.use((response) => {
  const t = response?.data?.session_token;
  if (t) authToken.set(t);
  return response;
}, (error) => {
  // Any ACTIVITY (write) attempted while signed-out returns 401 → send them to sign in.
  const status = error?.response?.status;
  const cfg = error?.config || {};
  const method = (cfg.method || "get").toLowerCase();
  const url = cfg.url || "";
  const isWrite = ["post", "put", "patch", "delete"].includes(method);
  const isAuthCall = url.includes("/auth/");
  if (status === 401 && isWrite && !isAuthCall && typeof window !== "undefined") {
    if (!window.location.pathname.startsWith("/auth")) {
      authToken.clear();
      rememberRedirect();
      toast.error("Please sign in to continue.");
      setTimeout(() => window.location.assign("/auth"), 700);
    }
  }
  // Out of credits on any activity → prompt to recharge ("Charge with credits to continue").
  if (status === 402 && typeof window !== "undefined") {
    const detail = error?.response?.data?.detail;
    const message = typeof detail === "string" ? detail : "Charge with credits to continue";
    window.dispatchEvent(new CustomEvent("open-recharge", { detail: { message } }));
  }
  return Promise.reject(error);
});

export function apiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (detail === "pending_approval") return "Your account is awaiting a quick welcome-approval from our team — you'll be able to do this very soon. 🙏";
  if (detail === "waitlisted") return "Our Soft Launch is currently full — you're on the waitlist. We'll email you the moment a spot opens. 🙏";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
